import type { Post } from "@/lib/news/types";
import heroAnmarkning from "@/assets/news/lan-betalningsanmarkning-30000.jpg";

/** Långivare som prövar utan UC och oftare accepterar äldre anmärkningar. */
const UTAN_UC = [
  "northmill", "credifi", "lumify", "ferratum", "saldo",
  "creditstar", "loanstep", "tomly", "merax", "flexkonto", "brixokk", "klaralan",
];

/** Förmedlare som skickar ansökan vidare till många banker på en upplysning. */
const FORMEDLARE = ["sambla", "advisa", "lendo", "enklare", "axo", "loansse"];

/** Alternativ när ett nytt lån inte är rätt väg. */
const ALTERNATIV = ["fairlo", "brixopl", "northmillsamla", "finlo", "banky"];

export const LAN_BETALNINGSANMARKNING: Record<string, Post> = {
  "lan-med-betalningsanmarkning-bast-i-test-30000-kr": {
    title: "Lån med betalningsanmärkning 2026 – bäst i test för 30 000 kr",
    intro:
      "Ett lån med betalningsanmärkning är svårare att få, men långt ifrån omöjligt. Här jämför vi alla långivare som prövar ansökningar trots anmärkning 2026, med ränta, avgifter, belopp och utbetalningstid – och visar vilka som blev bäst i test när behovet är 30 000 kr. Du får också veta exakt vad ett lån på 30 000 kr kostar per månad, vilka krav som gäller och vilka gratisalternativ som ofta löser situationen billigare.",
    author: "Erik Lindqvist",
    authorTitle: "Ekonomiredaktör",
    published: "2026-08-26",
    updated: "2026-08-26",
    heroSrc: heroAnmarkning,
    heroAlt: "Rådgivare och kund går igenom ansökan om lån med betalningsanmärkning på 30 000 kr",
    topLenderId: "northmill",
    bottomLenderId: "credifi",
    topLenderTitle: "Bäst i test – lån med betalningsanmärkning 2026",
    bottomLenderTitle: "Vår rekommendation vid tidigare avslag",
    reviewsLenderId: "credifi",
    cta: { to: "/lan-utan-uc", label: "Se hela topplistan för lån utan UC" },
    calculator: {
      title: "Räkna på lån med betalningsanmärkning – lånekalkylator 30 000 kr",
      intro:
        "Fyll i lånebelopp, löptid och ränta så ser du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta för ditt lån.",
      note:
        "Med betalningsanmärkning sätts räntan högre än normalt, vilket gör löptiden extra viktig. Ett lån på 30 000 kr med samma ränta kostar ungefär dubbelt så mycket i ränta på 48 månader som på 24. Jämför alltid effektiv ränta, eftersom uppläggningsavgift och aviavgift räknas in där – och låna aldrig mer än det belopp som faktiskt löser problemet.",
      amount: 30000,
      minAmount: 1000,
      maxAmount: 100000,
      months: 24,
      maxMonths: 96,
      rate: 19.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 395,
      invoiceFee: 29,
    },
    faq: [
      { q: "Kan man få lån med betalningsanmärkning 2026?", a: "Ja, men urvalet är begränsat. Långivare som prövar hos Creditsafe eller Dun & Bradstreet i stället för UC gör en egen helhetsbedömning där aktuell inkomst väger tyngre än historik. Är anmärkningen äldre och skulden reglerad är chansen betydligt bättre än om ärendet är pågående." },
      { q: "Går det att låna 30 000 kr med anmärkning?", a: "Det är möjligt, men de flesta långivare som accepterar anmärkning har ett tak på 50 000 kr och beviljar ofta ett lägre belopp än det du sökt. Med fast inkomst och en enstaka äldre anmärkning är 30 000 kr ett realistiskt belopp." },
      { q: "Vilken ränta får man med betalningsanmärkning?", a: "Räkna med 15–29 procent nominell ränta 2026. Räntetaket i konsumentkreditlagen innebär att räntan aldrig får överstiga Riksbankens referensränta plus 20 procentenheter, oavsett hur svag kreditvärdigheten är." },
      { q: "Hur länge ligger en betalningsanmärkning kvar?", a: "För privatpersoner tre år från det att ärendet avslutades. Den försvinner alltså inte när du betalar, men den väger allt lättare med tiden och de flesta långivare skiljer tydligt på färska och äldre anmärkningar." },
      { q: "Får jag lån om jag har ett pågående ärende hos Kronofogden?", a: "I princip aldrig. Vid löneutmätning är hela ditt överskott redan intecknat av myndigheten, och en långivare som ändå beviljade lån skulle bryta mot kravet på kreditprövning." },
      { q: "Hjälper en medsökande?", a: "Ja, mer än något annat du kan göra på kort sikt. Två inkomster i samma ansökan förbättrar kalkylen kraftigt och kan sänka räntan flera procentenheter. Medsökanden blir samtidigt solidariskt ansvarig för hela skulden." },
      { q: "Kan jag samla mina lån trots anmärkning?", a: "Ibland. Ett samlingslån kräver normalt god kreditvärdighet, men vissa långivare accepterar äldre anmärkningar om den samlade månadskostnaden tydligt sjunker genom lösenet." },
      { q: "Vad kostar 30 000 kr per månad?", a: "Med 19,9 procent nominell ränta, 395 kr uppläggning och 29 kr avi blir månadskostnaden cirka 1 555 kr på 24 månader och cirka 1 140 kr på 36 månader. Totalkostnaden blir då 8 700 kr respektive 12 800 kr." },
      { q: "Påverkas anmärkningen av att jag ansöker?", a: "Nej, ansökan i sig ändrar inte anmärkningen. Däremot registreras kreditupplysningen, och många förfrågningar under kort tid sänker din score ytterligare." },
      { q: "Finns det gratis hjälp?", a: "Ja. Budget- och skuldrådgivningen i din kommun är kostnadsfri och har tystnadsplikt. De hjälper med budget, kontakt med fordringsägare och ansökan om skuldsanering." },
    ],
    blocks: [
      { t: "p", text: "Har du en betalningsanmärkning vet du redan hur det känns att fylla i en ansökan och nästan räkna med ett nej. Den här sidan är skriven för att ge dig raka besked i stället för falskt hopp: vilka långivare som faktiskt prövar din ansökan, vad ett lån med betalningsanmärkning på 30 000 kr kostar 2026, och när något annat än ett nytt lån är den bättre vägen." },

      { t: "h2", text: "Lån med betalningsanmärkning – kort svar först" },
      { t: "p", text: "Det avgörande är inte om du har en anmärkning, utan om den är pågående eller avslutad, hur gammal den är och hur din inkomst ser ut i dag. Med en äldre, reglerad anmärkning och stabil inkomst finns det flera långivare som säger ja. Med pågående utmätning säger i princip alla nej." },
      {
        t: "stats",
        items: [
          { label: "Realistiskt belopp", value: "5 000–50 000 kr" },
          { label: "Ränta 2026", value: "15–29 %" },
          { label: "Anmärkning ligger kvar", value: "3 år" },
        ],
      },

      { t: "h2", text: "Bäst i test 2026 – långivare som prövar trots anmärkning" },
      { t: "p", text: "Långivarna nedan tar kreditupplysning hos Creditsafe eller Dun & Bradstreet i stället för hos UC och gör en egen helhetsbedömning. Det är här chansen är störst att få ett lån med betalningsanmärkning beviljat." },
      {
        t: "lenders",
        ids: UTAN_UC,
        title: "Topplista – lån med betalningsanmärkning 2026",
        note: "Räntorna är exempelräntor och sätts individuellt. Alla långivare i listan har tillstånd från Finansinspektionen. Den effektiva räntan beräknas enligt Konsumentverkets riktlinjer.",
      },

      { t: "h2", text: "Vad är en betalningsanmärkning?" },
      { t: "p", text: "En betalningsanmärkning är en notering i din kreditupplysning om att du inte betalat en skuld i tid och att ärendet gått hela vägen till utslag hos Kronofogden. Det är alltså inte inkassokravet i sig som ger anmärkningen, utan myndighetsbeslutet." },
      { t: "h3", text: "Från obetald räkning till anmärkning" },
      {
        t: "steps",
        title: "Kedjan i klartext",
        items: [
          "Räkningen förfaller och en påminnelseavgift tillkommer.",
          "Ärendet går till inkasso med lagstadgad inkassoavgift.",
          "Fordringsägaren ansöker om betalningsföreläggande hos Kronofogden.",
          "Du får ett brev och kan bestrida inom angiven tid.",
          "Bestrider du inte meddelas utslag – anmärkningen registreras.",
          "Kan du inte betala inleds utmätning av lön eller tillgångar.",
        ],
      },
      { t: "p", text: "Varje steg går att bryta. Ett samtal till fordringsägaren i steg ett eller två är den billigaste åtgärd som finns. Läs mer om processen hos [Kronofogden](https://www.kronofogden.se)." },

      { t: "h2", text: "Hur länge påverkar anmärkningen dina lånechanser?" },
      {
        t: "table",
        caption: "Så länge ligger uppgifterna kvar i kreditupplysningen",
        head: ["Uppgift", "Ligger kvar", "Påverkan på lån"],
        rows: [
          ["Inkassokrav som inte gått till KFM", "Syns inte", "Ingen direkt"],
          ["Betalningsanmärkning, privatperson", "3 år", "Hög"],
          ["Pågående löneutmätning", "Hela perioden", "Mycket hög"],
          ["Skuldsaneringsbeslut", "5 år", "Total spärr"],
          ["Avslutat ärende, skulden betald", "3 år från avslut", "Avtagande"],
        ],
      },

      { t: "h2", text: "Så bedömer långivaren din ansökan" },
      { t: "p", text: "Kreditgivaren gör en kvar att leva på-kalkyl: inkomst efter skatt minus boendekostnad, befintliga krediter och schabloner för mat, försäkringar och resor. Ryms den nya månadskostnaden med marginal blir svaret ofta ja, även med en äldre anmärkning i bagaget." },
      { t: "h3", text: "Vad som väger tyngst" },
      {
        t: "ul",
        items: [
          "Antal anmärkningar – en enstaka väger betydligt lättare än fem.",
          "Ålder på anmärkningen – över 18 månader gammal gör stor skillnad.",
          "Om skulden är reglerad eller fortfarande obetald.",
          "Inkomstens storlek och regelbundenhet.",
          "Antal kreditförfrågningar de senaste tolv månaderna.",
        ],
      },

      { t: "h2", text: "Chansen att bli beviljad – realistisk översikt" },
      {
        t: "table",
        caption: "Ungefärlig chans att få 30 000 kr beviljat 2026",
        head: ["Din situation", "Vanlig bank", "Långivare utan UC"],
        rows: [
          ["Pågående löneutmätning", "Ingen", "Mycket låg"],
          ["Pågående skuldsanering", "Ingen", "Ingen"],
          ["Anmärkning yngre än 12 mån", "Mycket låg", "Låg"],
          ["Anmärkning 1–3 år, skulden betald", "Låg", "Måttlig"],
          ["Enstaka äldre anmärkning, fast inkomst", "Låg", "God"],
          ["Inga anmärkningar, många förfrågningar", "Låg", "God"],
        ],
      },

      { t: "h2", text: "Vad kostar ett lån på 30 000 kr?" },
      { t: "p", text: "Exemplen nedan utgår från 19,9 procent nominell ränta, 395 kr uppläggningsavgift och 29 kr aviavgift – en realistisk nivå för ett lån med betalningsanmärkning 2026." },
      {
        t: "table",
        caption: "Räkneexempel: 30 000 kr, 19,9 % nominell ränta, 395 kr uppläggning, 29 kr avi",
        head: ["Löptid", "Månadskostnad", "Total lånekostnad", "Effektiv ränta"],
        rows: [
          ["12 mån", "2 811 kr", "3 733 kr", "24,7 %"],
          ["24 mån", "1 555 kr", "7 715 kr", "23,4 %"],
          ["36 mån", "1 143 kr", "11 543 kr", "22,9 %"],
          ["48 mån", "944 kr", "15 507 kr", "22,6 %"],
          ["60 mån", "829 kr", "19 585 kr", "22,4 %"],
        ],
      },
      { t: "p", text: "Skillnaden mellan 24 och 60 månader är nästan 12 000 kr i ren kostnad för exakt samma lån. Välj alltid den kortaste löptid din budget klarar med marginal." },

      { t: "h2", text: "Räntetak och kostnadstak – ditt lagliga skydd" },
      { t: "p", text: "Nominell ränta får aldrig överstiga Riksbankens referensränta plus 20 procentenheter, och de samlade kostnaderna får aldrig överstiga lånebeloppet. På 30 000 kr kan alltså ränta, avgifter och dröjsmålsränta tillsammans aldrig bli mer än 30 000 kr. Referensräntan fastställs av [Riksbanken](https://www.riksbank.se)." },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "steps",
        title: "Checklista innan du ansöker",
        items: [
          "Du är minst 18–20 år och folkbokförd i Sverige.",
          "Du har svenskt BankID för signering.",
          "Du har regelbunden och dokumenterbar inkomst.",
          "Du har inget pågående ärende hos Kronofogden.",
          "Du har räknat på månadskostnaden i kalkylatorn och satt ett tak.",
        ],
      },
      { t: "h3", text: "Vilka inkomster räknas?" },
      {
        t: "table",
        caption: "Inkomsttyper och hur de bedöms av långivare utan UC",
        head: ["Inkomsttyp", "Accepteras oftast", "Kommentar"],
        rows: [
          ["Fast anställning", "Ja", "Ger bäst villkor"],
          ["Visstid eller vikariat", "Ja", "Krav på anställningstid förekommer"],
          ["Egen företagare", "Ja", "Deklaration kan begäras"],
          ["A-kassa", "Ofta", "Bedöms individuellt"],
          ["Sjuk- eller aktivitetsersättning", "Ofta", "Räknas som stabil inkomst"],
          ["Pension", "Ja", "Övre åldersgräns kan finnas"],
          ["Studiemedel", "Sällan", "Räknas normalt inte"],
        ],
      },

      { t: "h2", text: "Låneförmedlare – en upplysning, många banker" },
      { t: "p", text: "Har du inga anmärkningar men många tidigare förfrågningar är en förmedlare oftast billigast. Din ansökan skickas till upp till 40 banker på en enda kreditupplysning och du väljer sedan fritt bland buden." },
      {
        t: "lenders",
        ids: FORMEDLARE,
        title: "Låneförmedlare för 30 000 kr – flera bud på en ansökan",
        note: "Förmedlarna tar UC, men bara en gång per ansökan. Räntorna är exempelräntor och sätts individuellt av respektive bank.",
      },

      { t: "h2", text: "Alternativ när ett nytt lån inte är rätt väg" },
      { t: "p", text: "Kontokrediter, räntesänkning på befintliga lån och samlingslån löser ibland problemet utan att skulden växer. De här aktörerna är värda att titta på först." },
      {
        t: "lenders",
        ids: ALTERNATIV,
        title: "Kontokredit, räntesänkning och samlingslån",
        note: "Alternativ till ett nytt lån. Villkoren sätts individuellt och kräver normalt att inga pågående ärenden finns hos Kronofogden.",
      },

      { t: "h2", text: "Så ansöker du steg för steg" },
      {
        t: "steps",
        title: "Från ansökan till utbetalning",
        items: [
          "Räkna ut månadskostnaden och bestäm ditt absoluta tak.",
          "Jämför minst tre långivare på effektiv ränta.",
          "Fyll i korrekta inkomstuppgifter – felaktigheter ger direkt avslag.",
          "Signera med BankID och invänta besked.",
          "Läs avtalet innan utbetalningen; ångerrätten är 14 dagar.",
        ],
      },

      { t: "h2", text: "Så ökar du chansen att bli beviljad" },
      {
        t: "ul",
        items: [
          "Ansök om ett lägre belopp än du egentligen vill ha.",
          "Lägg till en medsökande med stabil inkomst.",
          "Vänta minst tre månader efter senaste avslaget.",
          "Betala av små krediter och stäng oanvända kontokrediter.",
          "Se till att skulden bakom anmärkningen är reglerad.",
        ],
      },

      { t: "h2", text: "Vanliga misstag att undvika" },
      {
        t: "box",
        tone: "warning",
        title: "Undvik det här",
        items: [
          "Att skicka in tio ansökningar samma dag.",
          "Att jämföra nominell ränta i stället för effektiv.",
          "Att välja längsta löptiden för att månadskostnaden ser låg ut.",
          "Att betala en avgift i förskott för ett utlovat lån.",
          "Att tacka ja till erbjudanden du inte själv sökt upp.",
        ],
      },

      { t: "h2", text: "Så känner du igen en oseriös aktör" },
      { t: "p", text: "Alla svenska konsumentkreditgivare ska ha tillstånd från [Finansinspektionen](https://www.fi.se). Löften om garanterat lån oavsett kreditvärdighet, krav på förskottsavgift eller avsaknad av effektiv ränta i erbjudandet är tydliga varningssignaler." },

      { t: "h2", text: "Kan man samla lån trots betalningsanmärkning?" },
      { t: "p", text: "Ibland. Ett samlingslån kräver normalt god kreditvärdighet, men enstaka äldre anmärkningar accepteras av vissa aktörer om lösenet tydligt sänker din samlade månadskostnad. Vår guide om [att samla lån](/samla-lan) går igenom hur mycket du kan spara." },

      { t: "h2", text: "Skuldsanering – när det är rätt väg" },
      { t: "p", text: "Om skulderna är så stora att du inte kan betala dem inom överskådlig tid är skuldsanering ofta det enda hållbara. Du lever på existensminimum i normalt fem år och blir därefter skuldfri. Under pågående skuldsanering får du inte ta nya krediter." },

      { t: "h2", text: "Gratis hjälp du har rätt till" },
      { t: "p", text: "Budget- och skuldrådgivningen i din kommun är kostnadsfri, har tystnadsplikt och ställer inga krav. Rådgivarna hjälper dig med budget, kontakt med fordringsägare och ansökan om skuldsanering. Konsumentvägledningen och [Konsumentverket](https://www.konsumentverket.se) ger dessutom oberoende information om dina rättigheter." },

      { t: "h2", text: "Vanliga belopp och vad de kostar" },
      {
        t: "table",
        caption: "Månadskostnad vid 19,9 % nominell ränta och 36 månaders löptid",
        head: ["Lånebelopp", "Månadskostnad", "Total lånekostnad"],
        rows: [
          ["10 000 kr", "400 kr", "4 010 kr"],
          ["15 000 kr", "586 kr", "6 445 kr"],
          ["20 000 kr", "772 kr", "8 810 kr"],
          ["30 000 kr", "1 143 kr", "11 543 kr"],
          ["50 000 kr", "1 886 kr", "18 900 kr"],
        ],
      },

      { t: "h2", text: "Lån med betalningsanmärkning jämfört med vanligt privatlån" },
      {
        t: "proscons",
        title: "För- och nackdelar",
        pros: [
          "Prövning utan UC ger ny chans efter avslag",
          "Snabb hantering och ofta utbetalning samma dag",
          "Fler inkomsttyper accepteras",
          "Lagstadgat ränte- och kostnadstak skyddar dig",
        ],
        cons: [
          "Klart högre ränta än ett vanligt privatlån",
          "Lägre maxbelopp, ofta 50 000 kr",
          "Kortare löptider och högre månadskostnad",
          "Löser sällan orsaken till skulderna",
        ],
      },

      { t: "h2", text: "Relaterade guider" },
      { t: "p", text: "Läs vidare i vår genomgång av [lån utan UC](/lan-utan-uc), guiden om [lån 15000 utan UC](/nyhet/lan-15000-utan-uc) och artikeln om [sms lån trots Kronofogden](/nyhet/sms-lan-trots-kronofogden) om ditt ärende fortfarande är pågående." },

      { t: "h2", text: "Sammanfattning – bäst i test 2026" },
      { t: "p", text: "Northmill blev bäst i test för lån med betalningsanmärkning 2026 tack vare kreditprövning utan UC, tydliga villkor och snabb utbetalning, med Credifi som stark rekommendation vid tidigare avslag. Ett lån på 30 000 kr kostar normalt 1 100–1 600 kr per månad beroende på löptid. Räkna alltid på totalkostnaden först, låna det minsta belopp som löser problemet – och kontakta kommunens skuldrådgivning innan du tar ett nytt lån ovanpå ett gammalt." },
    ],
  },
};
